/**
 * Macro #26 · Territory 3D Draft API client (Increment 2).
 *
 * Consumes the CERTIFIED Increment-1 endpoints:
 *   GET  /api/v1/territory3d/{identity_id}/draft
 *   PUT  /api/v1/territory3d/{identity_id}/draft
 * Reuses the EXACT hardened owner authority (SIWE session cookie + memory-only
 * session-bound CSRF shared with the other management clients). The backend
 * remains the FINAL authority for capacity / occupancy / collision / Z / XY /
 * revision — the client never invents a second 3D truth.
 */
import type { WorkArt } from "../domain/voxel3d";
import { API_BASE } from "./api";
import { clearCsrfToken, getCsrfToken } from "./territory-identity-api";

export class Territory3DApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "Territory3DApiError";
    this.status = status;
    this.code = code;
  }
}

export interface Draft3DAuthority {
  identity_revision: number;
  membership_fingerprint: string;
}

export interface Draft3DCapacity {
  capacity_voxels: number;
  used_voxels: number;
  available_voxels: number;
  over_capacity: boolean;
  per_parcel?: Array<{ parcel_id: number; free_voxel_capacity: number; status: string; eligible: boolean }>;
}

export interface Draft3DDoc {
  identity_id: string;
  revision: number;
  work_arts: WorkArt[];
  authority: Draft3DAuthority;
  capacity: Draft3DCapacity;
}

interface ErrorEnvelope { code?: string; message?: string; detail?: { code?: string; message?: string } }

async function parse(resp: Response): Promise<unknown> {
  const text = await resp.text();
  let body: unknown = null;
  try { body = text ? JSON.parse(text) : null; } catch { body = null; }
  if (!resp.ok) {
    const env = (body ?? {}) as ErrorEnvelope;
    throw new Territory3DApiError(
      resp.status,
      env.code ?? env.detail?.code ?? "TERRITORY3D_ERROR",
      env.message ?? env.detail?.message ?? resp.statusText,
    );
  }
  return body;
}

export async function loadDraft3D(identityId: string, signal?: AbortSignal): Promise<Draft3DDoc> {
  const resp = await fetch(`${API_BASE}/api/v1/territory3d/${encodeURIComponent(identityId)}/draft`, {
    credentials: "include", cache: "no-store", signal,
  });
  return ((await parse(resp)) as { draft_3d: Draft3DDoc }).draft_3d;
}

export async function saveDraft3D(
  identityId: string,
  workArts: WorkArt[],
  authority: Draft3DAuthority,
  signal?: AbortSignal,
): Promise<Draft3DDoc> {
  const body = JSON.stringify({ work_arts: workArts, authority });
  const doPut = async (csrf: string): Promise<Response> =>
    fetch(`${API_BASE}/api/v1/territory3d/${encodeURIComponent(identityId)}/draft`, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json", "X-The-Grid-CSRF": csrf },
      body,
      signal,
    });
  let resp = await doPut(await getCsrfToken());
  if (resp.status === 403) {
    const peek = await resp.clone().json().catch(() => null);
    const code = peek?.code ?? peek?.detail?.code;
    if (code === "CSRF_INVALID" || code === "CSRF_REQUIRED") {
      clearCsrfToken();
      resp = await doPut(await getCsrfToken(true));
    }
  }
  return ((await parse(resp)) as { draft_3d: Draft3DDoc }).draft_3d;
}
